import { useCallback, useEffect, useRef, useState } from "react"

import { Flex, Typography } from "antd"
import { useTranslation } from "react-i18next"

import Button from "../../../../../../Components/Button"
import Spinner from "../../../../../../Components/Spinner"
import { getEvidencePage } from "../../../../api/services"
import EvidenceToolbar from "./EvidenceToolbar/EvidenceToolbar"
import PageCanvas from "./PageCanvas"

import detailStyles from "../../detail.module.less"
import styles from "./evidencePane.module.less"

const { Text } = Typography

// The evidence page renders at this fraction of the pane width by default, which
// reads as "100%" in the zoom control. Stepping zoom scales up/down from here.
const FIT_WIDTH = 0.75

// We display the evidence as flattened, watermarked PAGE IMAGES (not a PDF), so
// the merchant can scroll + zoom natively but cannot select/copy text, follow
// links, or pull the file. The clean, submittable PDF is never sent to the
// browser, and each page is painted onto a <canvas> (not an <img>) so there is
// no source URL to lift.
// Fetch a page and decode it straight to an ImageBitmap. We deliberately do NOT
// create an object URL: the bytes are drawn onto a <canvas>, so there is no
// `blob:` URL sitting in the DOM that a merchant could grab in DevTools and open
// in a new tab. The bitmap is closed when the page is replaced/unmounted.
const fetchPageBitmap = async (disputeId, version, page) => {
  const res = await getEvidencePage(disputeId, version, page)
  if (!res?.ok || !res?.data) return null
  const raw = res.data
  const blob = raw instanceof Blob ? raw : new Blob([raw], { type: "image/png" })
  try {
    return await createImageBitmap(blob)
  } catch (e) {
    return null
  }
}

// The evidence document for one version, plus its floating toolbar. Owns every piece
// of viewer state (decoded pages, zoom, current page) and loads the pages itself
// whenever the version it is asked to show changes.
//
// `active` rather than the caller rendering us conditionally: the page tabs would
// otherwise unmount this on every switch away and re-fetch every page bitmap on the
// way back. Returning null keeps the state without keeping the DOM.
const EvidencePane = ({
  active,
  disputeId,
  disputeStatus,
  version,
  onVersionChange,
  evidenceFiles,
  isActionable
}) => {
  const { t } = useTranslation()

  const [pages, setPages] = useState([]) // decoded ImageBitmaps (no blob URLs)
  const [pagesLoading, setPagesLoading] = useState(false)
  const [pagesError, setPagesError] = useState(false) // page fetch failed (vs. genuinely no evidence)
  const [pdfZoom, setPdfZoom] = useState(1) // width factor (1 = fit)
  const [pageIndex, setPageIndex] = useState(0) // current page in the viewer
  const pagesRef = useRef([])
  const canvasRef = useRef(null)
  // Monotonic token so a slow page-load for an old version can't overwrite (or
  // leak) the images of the version the merchant has since switched to.
  const loadTokenRef = useRef(0)

  // A plain number, so the load effect below re-runs when the page count of the
  // version on screen changes without depending on the evidence_files identity
  // (which is rebuilt on every edit to the dispute).
  const pageCount = (evidenceFiles.find((e) => e.version === version) || {}).page_count || 1

  const loadPages = useCallback(async () => {
    const token = ++loadTokenRef.current
    // Release the previous page bitmaps before swapping in new ones.
    pagesRef.current.forEach((b) => b && b.close && b.close())
    pagesRef.current = []
    setPages([])
    setPageIndex(0)
    setPagesError(false)
    if (!version) return
    setPagesLoading(true)
    try {
      const n = Math.max(1, pageCount)
      const bitmaps = await Promise.all(
        Array.from({ length: n }, (_, i) => fetchPageBitmap(disputeId, version, i))
      )
      // A newer load (e.g. version switch) started while we were fetching: drop these
      // results and close the bitmaps so they don't leak or clobber.
      if (token !== loadTokenRef.current) {
        bitmaps.forEach((b) => b && b.close && b.close())
        return
      }
      const ok = bitmaps.filter(Boolean)
      pagesRef.current = ok
      setPages(ok)
      // Distinguish a load failure from "no evidence": we asked for >=1 page for a
      // real version but got nothing back. The empty-state copy branches on this.
      setPagesError(ok.length === 0)
    } catch (e) {
      if (token === loadTokenRef.current) setPagesError(true)
    } finally {
      if (token === loadTokenRef.current) setPagesLoading(false)
    }
  }, [disputeId, version, pageCount])

  useEffect(() => {
    loadPages()
  }, [loadPages])

  useEffect(
    () => () => {
      // Invalidate any in-flight load so a post-unmount run cannot leak the decoded
      // ImageBitmaps.
      loadTokenRef.current++
      pagesRef.current.forEach((b) => b && b.close && b.close())
    },
    []
  )

  // Track which page is in view (for the toolbar page indicator) and jump to a page
  // when the merchant uses the prev/next controls.
  const handleCanvasScroll = () => {
    const c = canvasRef.current
    if (!c) return
    const imgs = c.querySelectorAll("[data-page]")
    if (!imgs.length) return
    const center = c.getBoundingClientRect().top + c.clientHeight / 2
    let idx = 0
    imgs.forEach((img, i) => {
      if (img.getBoundingClientRect().top <= center) idx = i
    })
    setPageIndex(idx)
  }

  const goToPage = (i) => {
    const c = canvasRef.current
    if (!c) return
    const imgs = c.querySelectorAll("[data-page]")
    const clamped = Math.max(0, Math.min(imgs.length - 1, i))
    if (imgs[clamped]) {
      imgs[clamped].scrollIntoView({ behavior: "smooth", block: "start" })
      setPageIndex(clamped)
    }
  }

  if (!active) return null

  return (
    <main className={detailStyles.main}>
      <div className={styles.canvas} ref={canvasRef} onScroll={handleCanvasScroll}>
        {pagesLoading ? (
          <Flex align="center" justify="center" style={{ height: "100%" }}>
            <Spinner />
          </Flex>
        ) : pages.length && disputeStatus !== "skipped" ? (
          // Watermarked pages painted onto <canvas> elements floating on the dotted
          // canvas: scrollable + zoomable, but no selectable text, no links, no
          // download, and crucially no `blob:` URL in the DOM that a merchant could
          // open in a new tab to grab the clean-looking page.
          <div
            className={styles.pages}
            style={{ width: `${Math.round(pdfZoom * FIT_WIDTH * 100)}%` }}
          >
            {pages.map((b, i) => (
              <PageCanvas key={i} bitmap={b} index={i} />
            ))}
          </div>
        ) : version && pagesError ? (
          // We HAVE an evidence version but its page images failed to load
          // (network/auth). That is a load failure, not "no evidence", so say so and
          // offer a retry instead of implying the document is missing.
          <Flex
            vertical
            align="center"
            justify="center"
            gap={8}
            style={{ height: "100%", padding: 24, textAlign: "center" }}
          >
            <Text strong>{t("Couldn't load the evidence pages")}</Text>
            <Text type="secondary">
              {t(
                "The document exists but its pages failed to load. Check your connection and try again."
              )}
            </Text>
            <Button onClick={loadPages} text={t("Retry")} />
          </Flex>
        ) : (
          // No evidence version at all. For a Skipped dispute (never fought) this is
          // expected, so explain it rather than showing a bug-like error.
          <Flex
            vertical
            align="center"
            justify="center"
            gap={4}
            style={{ height: "100%", padding: 24, textAlign: "center" }}
          >
            <Text strong>
              {disputeStatus === "skipped"
                ? t("No evidence was submitted")
                : t("PDF not available")}
            </Text>
            <Text type="secondary">
              {disputeStatus === "skipped"
                ? t("This dispute was never fought, so no evidence was submitted.")
                : t("There is no evidence document to display for this dispute.")}
            </Text>
          </Flex>
        )}
      </div>

      {pages.length || isActionable || evidenceFiles.length > 1 ? (
        <EvidenceToolbar
          pageCount={pages.length}
          pageIndex={pageIndex}
          onGoToPage={goToPage}
          pdfZoom={pdfZoom}
          onZoomChange={setPdfZoom}
          version={version}
          onVersionChange={onVersionChange}
          evidenceFiles={evidenceFiles}
        />
      ) : null}
    </main>
  )
}

export default EvidencePane
